import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, CalendarClock } from 'lucide-react';
import { getExchangeReadiness } from '../lib/api/tools.js';

/**
 * Per-exchange session-calendar readiness.
 *
 * "Venue is live" is an unenforced conjunction of adapter + timezone + hours +
 * current-year closures + forms. Set one and forget another and the venue
 * ingests while silently mis-dating every filing — the same shape as the
 * fast-lane coverage bug, where is_fast_lane without a content_needs row
 * STOPPED coverage with no error.
 *
 * The row that matters is a venue with announcements flowing and no session
 * calendar. Twelve venues were in that state on 2026-08-19.
 */
export function ExchangeReadinessPanel() {
  const query = useQuery({
    queryKey: ['exchange-readiness'],
    queryFn: getExchangeReadiness,
    refetchInterval: 120_000,
  });

  if (query.isLoading) {
    return (
      <div className="text-sm text-muted-foreground">
        Loading exchange readiness…
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="text-sm text-red-600">
        Could not load exchange readiness.
      </div>
    );
  }

  const { exchanges, alerting_count: alertingCount } = query.data;
  // Unseeded venues that are not ingesting are noise, not a finding.
  const rows = exchanges.filter((e) => e.ingesting || e.ready);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-5 w-5" />
        <h3 className="font-semibold">Exchange session-calendar readiness</h3>
      </div>

      {alertingCount > 0 ? (
        <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>{alertingCount}</strong>{' '}
            {alertingCount === 1 ? 'venue is' : 'venues are'} ingesting
            announcements without a verified session calendar. Filings from
            those venues are dated against defaults rather than their own
            market session. Seed hours and closures via the exchange-reference
            tools before relying on their dates.
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded border border-green-300 bg-green-50 p-3 text-sm text-green-900">
          <CheckCircle2 className="h-4 w-4" />
          Every ingesting venue has a verified session calendar.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase text-muted-foreground">
              <th className="py-2 pr-3">Exchange</th>
              <th className="py-2 pr-3 text-right">Anns</th>
              <th className="py-2 pr-3">TZ</th>
              <th className="py-2 pr-3">Hours</th>
              <th className="py-2 pr-3 text-right">Closures</th>
              <th className="py-2 pr-3 text-right">Forms</th>
              <th className="py-2">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => {
              const alerting = !e.ready && e.ingesting;
              return (
                <tr
                  key={e.code}
                  className={`border-b last:border-0 ${alerting ? 'bg-amber-50' : ''}`}
                >
                  <td className="py-2 pr-3 font-medium">{e.code}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {e.announcements.toLocaleString()}
                  </td>
                  <td className="py-2 pr-3">{e.has_timezone ? '✓' : '—'}</td>
                  <td className="py-2 pr-3">
                    {e.has_hours ? (e.hours_verified_at ? '✓' : '✓ unverified') : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {e.closures_this_year}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{e.forms}</td>
                  <td className={`py-2 ${alerting ? 'font-medium text-amber-900' : ''}`}>
                    {e.verdict}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Hours marked “unverified” were seeded from code constants rather than
        read from the exchange’s published page. Closures counts are confirmed
        rows for the current year only — staged proposals are excluded by
        design and never reach the live session path.
      </p>
    </div>
  );
}
